import EventEmitter from 'eventemitter3'
import { prettyStringifyJSON } from '../core/prettyStringifyJSON'

export class BrowserFS extends EventEmitter {
  constructor({ dirHandle }) {
    super()
    this.dirHandle = dirHandle
  }

  async getPublicURL(filePath) {
    const file = await this.getFile(filePath)
    if (!file) return null
    return URL.createObjectURL(file)
  }

  async isEmpty(dirPath) {
    const parts = dirPath.split('/').filter(Boolean)
    let current = this.dirHandle
    // descend into directory
    for (const part of parts) {
      try {
        current = await current.getDirectoryHandle(part)
      } catch (err) {
        if (err.name === 'NotFoundError') {
          // directory doesn't exist → treat as empty
          return true
        }
        throw err
      }
    }
    // check for first entry of any kind (file or directory)
    for await (const _ of current.entries()) {
      return false
    }
    return true
  }

  async list(dirPath, ext) {
    const parts = dirPath.split('/').filter(Boolean)
    let current = this.dirHandle
    // descend into target directory
    for (const part of parts) {
      try {
        current = await current.getDirectoryHandle(part)
      } catch (err) {
        if (err.name === 'NotFoundError') return [] // silently return empty if path missing
        throw err
      }
    }
    // collect entries
    const entries = []
    for await (const [name, handle] of current.entries()) {
      if (ext && !name.endsWith(ext)) continue
      entries.push(name)
    }
    return entries
  }

  async getFile(filePath) {
    const parts = filePath.split('/').filter(Boolean)
    if (parts.length === 0) throw new Error('Path required')
    // navigate down
    let current = this.dirHandle
    for (let i = 0; i < parts.length - 1; i++) {
      try {
        current = await current.getDirectoryHandle(parts[i])
      } catch (err) {
        if (err.name === 'NotFoundError') return null // directory missing
        throw err
      }
    }
    // try to get the file handle
    try {
      const fileHandle = await current.getFileHandle(parts.at(-1))
      return await fileHandle.getFile() // → returns a File instance
    } catch (err) {
      if (err.name === 'NotFoundError') return null // file missing
      throw err
    }
  }

  async getFileData(filePath) {
    const file = await this.getFile(filePath)
    if (!file) return null
    return new Uint8Array(await file.arrayBuffer())
  }

  async getJSON(filePath) {
    try {
      const file = await this.getFile(filePath)
      if (!file) return null
      const text = await file.text()
      return JSON.parse(text)
    } catch (err) {
      return null
    }
  }

  async getText(filePath) {
    try {
      const file = await this.getFile(filePath)
      if (!file) return null
      return await file.text()
    } catch (err) {
      return null
    }
  }

  async insertFile(filePath, data) {
    // normalize and split path
    const parts = filePath.split('/').filter(Boolean) // remove "" from leading/trailing slashes
    if (parts.length === 0) throw new Error('Path must include a file name')
    // walk/create intermediate directories
    let current = this.dirHandle
    for (let i = 0; i < parts.length - 1; i++) {
      const segment = parts[i]
      current = await current.getDirectoryHandle(segment, { create: true })
    }
    // create (or open) file handle
    const filename = parts[parts.length - 1]
    const fileHandle = await current.getFileHandle(filename, { create: true })
    // create writable stream; default truncates/overwrites existing content
    const writable = await fileHandle.createWritable({ keepExistingData: false })
    // accept File, Blob, ArrayBuffer, or TypedArray
    if (data instanceof Blob) {
      await writable.write(data)
    } else if (data instanceof ArrayBuffer) {
      await writable.write(new Uint8Array(data))
    } else if (ArrayBuffer.isView(data)) {
      await writable.write(data)
    } else {
      throw new Error('Data must be a Blob/File, ArrayBuffer, or TypedArray')
    }
    await writable.close()
    // return fileHandle // optional: return the handle to the created/updated file
  }

  async insertJSON(filePath, data) {
    // const json = JSON.stringify(data, null, 2)
    const json = prettyStringifyJSON(data)
    const blob = new Blob([json], { type: 'application/json' })
    return await this.insertFile(filePath, blob)
  }

  async insertText(filePath, text) {
    const blob = new Blob([text], { type: 'text/plain' })
    return await this.insertFile(filePath, blob)
  }

  async deleteFile(filePath) {
    // normalize: remove empty / segments
    const parts = filePath.split('/').filter(Boolean)
    if (parts.length === 0) return // nothing to do
    // walk down to parent directory; bail silently if any segment is missing
    let current = this.dirHandle
    for (let i = 0; i < parts.length - 1; i++) {
      try {
        current = await current.getDirectoryHandle(parts[i])
      } catch (err) {
        if (err && err.name === 'NotFoundError') return // parent path missing → done
        throw err // other errors should surface
      }
    }
    // try to remove the final entry (file). If it's gone already, that's fine.
    const leaf = parts[parts.length - 1]
    try {
      await current.removeEntry(leaf)
    } catch (err) {
      if (err && err.name === 'NotFoundError') return // file missing → done
      // If you might hit a directory here and want to remove it too, use:
      // await current.removeEntry(leaf, { recursive: true });
      // and optionally swallow TypeMismatchError as well.
      throw err
    }
  }
}
